import { computed } from 'vue'
import { useUserGeo } from '~/composables/useUserGeo'
import { useTypesenseHandle } from '~/composables/search/useTypesenseHandle'
import { toGeoCoords } from '~/utils/typesenseGeo'
import {
  SEARCH_CITY_STORAGE_KEY,
  buildIpCitySearch,
  parseStoredSearchCity,
  toSearchCity,
  type SearchCity,
  type SearchCitySource,
} from '~/utils/searchCity'

const writeStored = (city: SearchCity | null | undefined) => {
  try {
    if (city === undefined) localStorage.removeItem(SEARCH_CITY_STORAGE_KEY)
    else localStorage.setItem(SEARCH_CITY_STORAGE_KEY, JSON.stringify({ city }))
  } catch {
    // Storage blocked (private mode): the choice just isn't remembered.
  }
}

const readStored = () => {
  try {
    return parseStoredSearchCity(localStorage.getItem(SEARCH_CITY_STORAGE_KEY))
  } catch {
    return undefined
  }
}

/**
 * City the homepage hero search is scoped to (KFT-4205), shared by the city
 * line, the picker and the search itself.
 *
 * Client-only, like the geo it rests on (KFT-3753): CloudFront caches the
 * homepage without cookies, so SSR always renders no city and the line fills
 * in after `init()`. The IP city is the default; an explicit pick (a city, or
 * "Überall") is remembered in localStorage and wins over it.
 */
export const useSearchCity = () => {
  const city = useState<SearchCity | null>('search-city', () => null)
  const source = useState<SearchCitySource>('search-city-source', () => 'none')
  const ipCity = useState<SearchCity | null>('search-city-ip', () => null)
  // Until init settles the line shows nothing, rather than flashing
  // "Überall suchen" before the IP city arrives.
  const isResolved = useState('search-city-resolved', () => false)
  const isInitStarted = useState('search-city-init', () => false)
  // Read during setup: runtime config needs the Nuxt context, gone after an await.
  const { host, apiKey } = useTypesenseHandle()

  const resolveIpCity = async (): Promise<SearchCity | null> => {
    const coords = toGeoCoords(await useUserGeo().getUserGeo())
    if (!coords) return null
    try {
      const { typesenseSearch } = await import('~/utils/typesenseClient')
      const res = await typesenseSearch<any>(buildIpCitySearch(coords), {
        host,
        apiKey,
      })
      return toSearchCity(res.hits?.[0]?.document)
    } catch (error) {
      console.error('[useSearchCity] IP city lookup failed', error)
      return null
    }
  }

  const init = async () => {
    if (!import.meta.client || isInitStarted.value) return
    isInitStarted.value = true

    const stored = readStored()
    if (stored) {
      city.value = stored.city
      source.value = stored.city ? 'user' : 'none'
      isResolved.value = true
    }
    // Resolved even when a stored choice exists: "In meiner Nähe" needs it.
    ipCity.value = await resolveIpCity()
    if (!stored) {
      city.value = ipCity.value
      source.value = ipCity.value ? 'ip' : 'none'
    }
    isResolved.value = true
  }

  const selectCity = (next: SearchCity) => {
    city.value = next
    source.value = 'user'
    writeStored(next)
  }

  /** × and "Überall": search everywhere, remembered like a pick. */
  const clearCity = () => {
    city.value = null
    source.value = 'none'
    writeStored(null)
  }

  /** "In meiner Nähe": forget the pick and fall back to the IP city. */
  const resetToIpCity = () => {
    writeStored(undefined)
    city.value = ipCity.value
    source.value = ipCity.value ? 'ip' : 'none'
  }

  return {
    city: computed(() => city.value),
    source: computed(() => source.value),
    ipCity: computed(() => ipCity.value),
    isResolved: computed(() => isResolved.value),
    init,
    selectCity,
    clearCity,
    resetToIpCity,
  }
}
